import React, { useEffect, useMemo } from "react";
import { SectionProps } from "../Sections/SectionProps";
import { ItemState } from "../../redux/state";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useAuth } from "@ofqual/portal-core/core/hooks/useAuth";
import { authSelector } from "../../redux/selectors";
import {
  modelToJsonPatchDocument,
  getLookupTracker,
  buildMetaData,
} from "@ofqual/portal-core/core/utils/modelHelpers";
import { ItemDetails } from "./ItemDetails";
import { Prompt} from "react-router-dom";
import { ModuleAppBar } from "../ModuleAppBar";
import { AppBarSaveButton } from "@ofqual/portal-components/components/SecondaryAppBar";

import { GuidanceDrawer } from "@ofqual/portal-components/components/Drawer";
import { guidanceService } from "../../services";
import { ExperimentalFeature } from "@ofqual/portal-components/components/ExperimentalFeature";
import { SarsModel, SarsStatus } from "../../domain/SarsModel";
import { updatingItem } from "../../redux/actions";
import { ShareButtonAndDialog } from "./ShareButtonAndDialog";
import { SelectStateButton } from "./SelecteState";

type NonNullableItemState = ItemState & {
  item: Exclude<ItemState["item"], null>;
};


export const ItemForm: React.FC<{
  state: NonNullableItemState;
  handleDrawerToggle: (isOpen: boolean) => void;
  open: boolean;
}> = ({
  state: { item, status, lastCrudResult, attachmentsToDelete },
  handleDrawerToggle,
  open,
}) => {
  const {
    handleSubmit,
    control,
    formState,
    errors,
    setValue,
    setError,
    clearErrors,
    getValues,
    reset,
  } = useForm<SarsModel>({
    defaultValues: item,
    shouldUnregister: false,
  });

  const dispatch = useDispatch();

  const { token, experimentalFeatures } = useAuth(authSelector);

  // on save (detected by a new item in redux) we need to clear dirtyFields, so reset is required
  useEffect(() => {
    reset(item);
  }, [reset, item]);

  const isRejected = item.status === SarsStatus.Rejected;
 
  const disabled =
    status === "UPDATING" ||
    item.status === SarsStatus.Closed ||
    item.status === SarsStatus.Rejected;

  // this is important for dirtyFields to be deconstructed/accessed prior to the onSubmit process
  const { dirtyFields } = formState;
  const lookupTracker = useMemo(() => getLookupTracker(), []);

  const onSubmit = (newModel: SarsModel) => {
    const fieldsToIgnore: (keyof SarsModel)[] = ["files"];

    const changedFields = (Object.keys(
      dirtyFields
    ) as (keyof SarsModel)[]).filter((x) => !fieldsToIgnore.includes(x));

    let document = modelToJsonPatchDocument(newModel, changedFields);

    const meta = buildMetaData(newModel, item, changedFields, lookupTracker);
    dispatch(
      updatingItem(
        item.id,
        { document, meta },
        newModel.files,
        attachmentsToDelete
      )
    );
  };

  const sectionProps: SectionProps<SarsModel> = {
    isCreateMode: false,
    disabled,
    control,
    errors,
    formState,
    item,
    clearErrors,
    setValue: (name, value) => setValue(name, value),
    getValues,
    registerCustomError: (name: string, isError: boolean) => {
      const isCurrentlyARegisteredError = Object.keys(errors).includes(name);
      if (isError && !isCurrentlyARegisteredError) {
        setError(name, {});
      }
      if (!isError && isCurrentlyARegisteredError) {
        clearErrors(name);
      }
    },
    lookupTracker,
  };

 
 
  const mappedGuidanceService = useMemo(
    () => ({
      get: () => {
        return guidanceService.get(
          { module: "sars", key: "edit-sars" },
          { token }
        );
      },
    }),
    [token]
  );


  return (
    <>
      <Prompt
        message="There are unsaved changes in the form.  Are you sure you want to navigate away from this page and lose these changes?"
        when={Object.keys(formState.dirtyFields).length > 0}
      />
      <ModuleAppBar
        breadcrumbs={[
          { label: "List", to: "List" },
          { label: `${item.caseTitle}` },
        ]}
      >
        <AppBarSaveButton
          onClick={handleSubmit(onSubmit)}
          isSubmitting={status === "UPDATING"}
          isSubmitted={lastCrudResult.isSuccess}
          dataTest="save"
          disabled={disabled}
        />
        <ShareButtonAndDialog id={item.id} />

        {!isRejected ? <SelectStateButton sar={item} /> : null}

        <ExperimentalFeature
          experimentalModuleName="portal-sars"
          experimentalFeatures={experimentalFeatures}
          experimentalFeatureVisibleName="sarsGuidancecomplete"
        >
          <GuidanceDrawer
            open={open}
            handleDrawerToggle={handleDrawerToggle}
            service={mappedGuidanceService}
          />
        </ExperimentalFeature>
      </ModuleAppBar>
      <form>
        <ItemDetails sectionProps={sectionProps} />
      </form>
    </>
  );
};
